function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

function showLoader() {
  document.getElementById("delivery-loader").style.display = "flex";
}

function hideLoader() {
  document.getElementById("delivery-loader").style.display = "none";
}

/**
 * Shopify expects E.164 phone numbers (e.g. +61412345678 for AU).
 * The form collects 10-digit local numbers like 0412345678.
 */
function formatAustralianPhone(phone, countryCode = 'AU') {
  const raw = String(phone || '').trim();
  if (!raw) {
    return '';
  }

  if (raw.startsWith('+')) {
    const digits = raw.slice(1).replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  }

  const digits = raw.replace(/\D/g, '');
  if (!digits) {
    return '';
  }

  if (countryCode.toUpperCase() === 'AU') {
    if (digits.startsWith('0') && digits.length === 10) {
      return `+61${digits.slice(1)}`;
    }

    if (digits.length === 9) {
      return `+61${digits}`;
    }

    if (digits.startsWith('61') && digits.length >= 11) {
      return `+${digits}`;
    }

    if (digits.length === 10 && digits.startsWith('1')) {
      return `+61${digits}`;
    }
  }

  return `+${digits}`;
}

function getSavedDeliveryFormData() {
  try {
    return JSON.parse(localStorage.getItem('delivery_form_data') || '{}');
  } catch (error) {
    return {};
  }
}

function getDeliveryFormAddress(form) {
  const savedData = getSavedDeliveryFormData();

  const readField = (selector, savedKey) => {
    const value = form?.querySelector(selector)?.value?.trim() || '';
    if (value) {
      return value;
    }

    return savedData[savedKey] || '';
  };

  const firstName = readField('input[name="contact_first_name"]', 'contact_first_name');
  const lastName = readField('input[name="contact_last_name"]', 'contact_last_name');
  const rawPhone = readField('input[name="contact_number"]', 'contact_number');

  return {
    email: readField('input[name="contact_email"]', 'contact_email'),
    first_name: firstName,
    last_name: lastName,
    name: [firstName, lastName].filter(Boolean).join(' '),
    phone: formatAustralianPhone(rawPhone),
    company_name: readField('input[name="company_name"]', 'company_name'),
    residential_type:
      form?.querySelector('input[name="residential_type_value"]:checked')?.value ||
      savedData.residential_type_value ||
      '',
    streetName: readField('input[name="delivery_address"]', 'delivery_address'),
    locality: readField('input[name="city"]', 'city'),
    state: readField('input[name="state"]', 'state'),
    postcode: readField('input[name="delivery_postal_code"]', 'delivery_postal_code'),
    country: 'AU',
    lift_available:
      form?.querySelector('input[name="lift_available"]:checked')?.value ||
      savedData.lift_available ||
      '',
    floor_level:
      form?.querySelector('input[name="floor_level"]:checked')?.value ||
      savedData.floor_level ||
      '',
    floor_level_other: readField('input[name="floor_level_other"]', 'floor_level_other'),
    special_info: readField('textarea[name="special_info"]', 'special_info'),
  };
}

function setRequestQuoteButtonLoading(isLoading) {
  const btn = document.getElementById('createRequestOrder');
  if (!btn) {
    return;
  }

  btn.disabled = isLoading;

  const btnText = btn.querySelector('.btn-text');
  const btnLoader = btn.querySelector('.btn_loader_request');

  if (btnText) {
    btnText.style.display = isLoading ? 'none' : '';
  }

  if (btnLoader) {
    btnLoader.style.display = isLoading ? 'inline-block' : 'none';
  }
}


// document.querySelector('.confirm-btn').addEventListener('click', async function (e) {
/*document.querySelector('#delivery-form').addEventListener('submit', async function (e) {

  e.preventDefault();
  
  if (!this.checkValidity()) {
    this.reportValidity();
    return;
  }

   showLoader();
  try {

    const form = document.querySelector('#delivery-form');

    const deliveryAddress = {
      residential_type: form.querySelector('input[name="residential_type_value"]')?.value || "",
      name: form.querySelector('input[name="contact_name"]')?.value || "",
      streetName: form.querySelector('input[name="delivery_address"]')?.value || "",
      companyName: form.querySelector('input[name="company_name"]')?.value || "",
      locality: form.querySelector('input[name="city"]')?.value || "",
      state: form.querySelector('input[name="state"]')?.value || "",
      postcode: form.querySelector('input[name="delivery_postal_code"]')?.value || "",
      country: "AU"
    };

    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    const cartItems = cart.items.map(item => {
      return {
        product_id: item.product_id,
        variant_id: item.variant_id,
        sku: item.sku,
        quantity: item.quantity,
        grams: item.grams,
      };
    });
    

  console.log("cartItems", cartItems);

    const payload = {
      deliveryAddress: deliveryAddress,
      cartItems: cartItems,
      shop : form.querySelector('input[name="shop_name"]')?.value
    };
    

    console.log("Sending Payload:", payload);

    const response = await fetch('https://api.auslock.com.au/api/get-quotes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();
    if(result) {
      renderQuotes(result);
      hideLoader();
      document.querySelector('.confirm-btn').style.display = 'none';

      console.log("Freight Quote:", result);
    } else {
      hideLoader();
      renderQuotes('');
      // alert("Error calculating freight, Please try again later.");
      //document.querySelector('#deliveryplaceorderBtn').style.display = 'block';
    }
  } catch (error) {
    console.error(error);
    hideLoader();
    renderQuotes('');
    //document.querySelector('#deliveryplaceorderBtn').style.display = 'block';
    // alert("Error calculating freight, Please try again later.");
  }

});*/

const deliveryForm = document.querySelector('#delivery-form');

if (deliveryForm) {
  deliveryForm.addEventListener('submit', async function (e) {
    e.preventDefault();

    if (!this.checkValidity()) {
      this.reportValidity();
      return;
    }

    showLoader();

    try {
      const form = document.querySelector('#delivery-form');

      const deliveryAddress = getDeliveryFormAddress(form);

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const cartItems = cart.items.map(item => {
        return {
          product_id: item.product_id,
          variant_id: item.variant_id,
          sku: item.sku,
          quantity: item.quantity,
          grams: item.grams,
        };
      });

      console.log("cartItems", cartItems);

      const payload = {
        deliveryAddress: deliveryAddress,
        cartItems: cartItems,
        shop: form.querySelector('input[name="shop_name"]')?.value || ""
      };

      console.log("Sending Payload:", payload);

      const response = await fetch('https://api.auslock.com.au/api/get-quotes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result) {
        renderDeliveryInstructions();
        renderQuotes(result);
        hideLoader();

        const confirmBtn = document.querySelector('.confirm-btn');
        if (confirmBtn) {
          confirmBtn.style.display = 'none';
        }

        console.log("Freight Quote:", result);
      } else {
        hideLoader();
        renderQuotes('');
      }

    } catch (error) {
      console.error(error);
      hideLoader();
      renderQuotes('');
    }
  });
}

document
  .querySelectorAll('#delivery-form input[name="residential_type_value"]')
  .forEach((radio) => {
    radio.addEventListener('change', function () {
      resetDeliveryShippingQuotesUI();
      resetDeliveryInstructions();
    });
  });



/*document.querySelector('.close-modal').onclick = function(){
  document.getElementById('delivery-popup').style.display = 'none';
};*/
document.addEventListener('DOMContentLoaded', function () {
  const closeBtn = document.querySelector('.close-modal');
  const popup = document.getElementById('delivery-popup');

  if (closeBtn && popup) {
    closeBtn.onclick = function () {
      popup.style.display = 'none';
    };
  }
});

document.querySelectorAll('input[name="delivery_type"]').forEach(radio => {
  radio.addEventListener('change', function(){

    localStorage.setItem('popup_delivery_type', this.value);
    localStorage.removeItem('delivery_flow_complete');
    localStorage.removeItem('shipping_unavailable');
    localStorage.removeItem('shipping_service_price');
    localStorage.removeItem('shipping_service_name');

    const installationSection = document.getElementById('installation-extra');

    document.querySelectorAll('.delivery-form').forEach(form=>{
      form.style.display = 'none';
    });

    // if(this.value === 'pickup'){
    //   installationSection.style.display = 'none';
    //   document.getElementById('pickup-form').style.display = 'block';
    // }

    const btn = document.querySelector('.confirm-btn');
    const vendorWarning = document.querySelector('.vendor-warning');
    
    if(this.value === 'delivered'){

      installationSection.style.display = 'none';
      if (btn) {
          btn.style.display = 'block';
      }

      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          const vendors = new Set();

          cart.items.forEach(item => {
            if (item.vendor) {
              vendors.add(item.vendor);
            }
          });

          console.log([...vendors]);

          if (vendors.size > 1) {
            vendorWarning.style.display = 'block';
          }
        });


      //document.querySelector('.confirm-btn').style.display = 'block';
      document.getElementById('delivery-form').style.display = 'block';
    }

    if(this.value === 'delivery_install'){
      installationSection.style.display = 'block';
      if (btn) {
          btn.style.display = 'none';
      }
      if (vendorWarning) {
          vendorWarning.style.display = 'none';
      }
      
      //document.querySelector('.confirm-btn').style.display = 'none';
      //document.querySelector('#deliveryplaceorderBtn').style.display = 'none';
      document.getElementById('delivery-form').style.display = 'block';
      const container = document.getElementById('shippingQuotes');
      container.innerHTML = '';
      const containerInstructions = document.getElementById('deliveryInstructions');
      containerInstructions.innerHTML = '';
      document.querySelector('#placeOrderBtn').style.display = 'none';
    }
  });
});

// document.getElementById('open-delivery-popup').onclick = function(){
//   document.getElementById('delivery-popup').style.display = 'block';

//   // Default selection: Pickup
//   const pickupRadio = document.querySelector('input[name="delivery_type"][value="pickup"]');
//   pickupRadio.checked = true;

//   // Hide all forms
//   document.querySelectorAll('.delivery-form').forEach(form=>{
//     form.style.display = 'none';
//   });

//   // Show pickup form
//   document.getElementById('pickup-form').style.display = 'block';
// };

document.addEventListener('click', function(e) {
  if (e.target.closest('#open-delivery-popup')) {

    document.getElementById('delivery-popup').style.display = 'flex';

    const savedPopupType = localStorage.getItem('popup_delivery_type') || 'delivered';
    const popupRadio = document.querySelector(`input[name="delivery_type"][value="${savedPopupType}"]`);
    const deliveredRadio = document.querySelector('input[name="delivery_type"][value="delivered"]');

    if (popupRadio) {
      popupRadio.checked = true;
    } else if (deliveredRadio) {
      deliveredRadio.checked = true;
    }

    const installationSection = document.getElementById('installation-extra');
    const confirmBtn = document.querySelector('.confirm-btn');

    if (savedPopupType === 'delivery_install') {
      if (installationSection) installationSection.style.display = 'block';
      if (confirmBtn) confirmBtn.style.display = 'none';
    } else {
      if (installationSection) installationSection.style.display = 'none';
      if (confirmBtn) confirmBtn.style.display = 'block';
    }

    document.querySelectorAll('.delivery-form').forEach(form => {
      form.style.display = 'block';
    });
  }
});

document.addEventListener('DOMContentLoaded', function () {
  initDeliveryPlacesAutocomplete();
});

const GOOGLE_PLACES_API_KEY = window.AUSLOCK_GOOGLE_MAPS_KEY || 'AIzaSyAbQ5EeTOgMPxVm8JCoOSFPnQfJgDQu5-Q';
const GOOGLE_PLACES_AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const GOOGLE_PLACES_DETAILS_URL = 'https://places.googleapis.com/v1/places/';

function createDeliveryPlacesSessionToken() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function resolveDeliveryPrimaryTypes(type, query) {
  if (type === 'address') {
    return null;
  }

  if (/^\d+$/.test(query)) {
    return ['postal_code'];
  }

  return ['(regions)'];
}

function getGoogleAddressComponent(components, type, shortName = false) {
  for (const component of components || []) {
    if (!(component.types || []).includes(type)) {
      continue;
    }

    return shortName ? (component.shortText || '') : (component.longText || '');
  }

  return '';
}

function parseGooglePlaceDetails(place) {
  const components = place.addressComponents || [];
  const streetNumber = getGoogleAddressComponent(components, 'street_number');
  const route = getGoogleAddressComponent(components, 'route');
  const suburb = getGoogleAddressComponent(components, 'locality')
    || getGoogleAddressComponent(components, 'postal_town')
    || getGoogleAddressComponent(components, 'sublocality');
  const state = getGoogleAddressComponent(components, 'administrative_area_level_1', true);
  const postcode = getGoogleAddressComponent(components, 'postal_code');
  const streetAddress = `${streetNumber} ${route}`.trim();
  const suburbUpper = suburb.toUpperCase();
  const stateUpper = state.toUpperCase();

  return {
    formatted_address: place.formattedAddress || '',
    street_number: streetNumber,
    route,
    street_address: streetAddress,
    suburb: suburbUpper,
    state: stateUpper,
    postcode,
    city: suburbUpper,
    delivery_postal_code: postcode,
    location_label: `${suburbUpper} ${stateUpper} ${postcode}`.trim(),
    location_value: `${suburbUpper}|${stateUpper}|${postcode}|`,
  };
}

async function fetchDeliveryPlacesAutocomplete(query, type, sessionToken) {
  const payload = {
    input: query,
    includedRegionCodes: ['au'],
    regionCode: 'au',
    languageCode: 'en',
    sessionToken,
  };

  const primaryTypes = resolveDeliveryPrimaryTypes(type, query);

  if (primaryTypes) {
    payload.includedPrimaryTypes = primaryTypes;
  }

  const response = await fetch(GOOGLE_PLACES_AUTOCOMPLETE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
      'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat',
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.error?.message || 'Google Places autocomplete failed.');
  }

  return (data.suggestions || [])
    .map((item) => {
      const prediction = item.placePrediction;

      if (!prediction) {
        return null;
      }

      return {
        place_id: prediction.placeId || '',
        label: prediction.text?.text || '',
        main_text: prediction.structuredFormat?.mainText?.text || '',
        secondary_text: prediction.structuredFormat?.secondaryText?.text || '',
      };
    })
    .filter(Boolean);
}

async function fetchDeliveryPlaceDetails(placeId) {
  const normalizedPlaceId = String(placeId || '').replace(/^places\//, '');
  const response = await fetch(`${GOOGLE_PLACES_DETAILS_URL}${encodeURIComponent(normalizedPlaceId)}`, {
    headers: {
      'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
      'X-Goog-FieldMask': 'id,formattedAddress,addressComponents',
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.error?.message || 'Google Places details failed.');
  }

  return parseGooglePlaceDetails(data);
}

function initDeliveryPlacesAutocomplete() {
  const streetInput = document.getElementById('delivery-address-input');
  const postcodeInput = document.getElementById('location-search');

  if (streetInput) {
    setupDeliveryPlacesField({
      input: streetInput,
      suggestionsBox: document.getElementById('delivery-address-suggestions'),
      type: 'address',
      fillStreet: true,
    });
  }

  if (postcodeInput) {
    setupDeliveryPlacesField({
      input: postcodeInput,
      suggestionsBox: document.getElementById('location-suggestions'),
      type: 'postcode',
      fillStreet: false,
    });
  }
}

function setupDeliveryPlacesField({ input, suggestionsBox, type, fillStreet }) {
  if (!input || !suggestionsBox) {
    return;
  }

  let debounceTimeout = null;
  let sessionToken = createDeliveryPlacesSessionToken();

  input.addEventListener('input', function () {
    clearTimeout(debounceTimeout);

    debounceTimeout = setTimeout(async () => {
      const query = input.value.trim();

      if (query.length < 2) {
        suggestionsBox.innerHTML = '';
        return;
      }

      try {
        const predictions = await fetchDeliveryPlacesAutocomplete(query, type, sessionToken);

        suggestionsBox.innerHTML = '';

        predictions.forEach((item) => {
          const div = document.createElement('div');
          div.classList.add('suggestion-item');

          if (item.secondary_text) {
            div.innerHTML = `<strong>${item.main_text || item.label}</strong> <small>${item.secondary_text}</small>`;
          } else {
            div.textContent = item.label;
          }

          div.addEventListener('click', async function () {
            suggestionsBox.innerHTML = '';

            try {
              const details = await fetchDeliveryPlaceDetails(item.place_id);
              sessionToken = createDeliveryPlacesSessionToken();
              applyDeliveryPlaceDetails(details, { input, fillStreet, selectedLabel: item.label });
            } catch (error) {
              console.error('Delivery place details error:', error.message);
            }
          });

          suggestionsBox.appendChild(div);
        });

        resetDeliveryShippingQuotesUI();
      } catch (error) {
        console.error('Delivery autocomplete error:', error.message);
      }
    }, 400);
  });

  document.addEventListener('click', function (event) {
    if (!suggestionsBox.contains(event.target) && event.target !== input) {
      suggestionsBox.innerHTML = '';
    }
  });
}

// function applyDeliveryPlaceDetails(details, { input, fillStreet, selectedLabel }) {
//   const cityInput = document.querySelector('input[name="city"]');
//   const postcodeInput = document.querySelector('input[name="delivery_postal_code"]');
//   const stateInput = document.querySelector('input[name="state"]');
//   const locationSearch = document.getElementById('location-search');
//   const hiddenInput = document.getElementById('location_value');
//   const streetInput = document.querySelector('input[name="delivery_address"]');

//   if (fillStreet && streetInput) {
//     streetInput.value = details.street_address || selectedLabel || '';
//   } else if (input === streetInput) {
//     input.value = details.street_address || selectedLabel || '';
//   }

//   if (cityInput && details.city) {
//     cityInput.value = details.city;
//   }

//   if (stateInput && details.state) {
//     stateInput.value = details.state;
//   }

//   if (postcodeInput && details.postcode) {
//     postcodeInput.value = details.postcode;
//   }

//   if (locationSearch && details.location_label) {
//     locationSearch.value = details.location_label;
//   }

//   if (hiddenInput && details.location_value) {
//     hiddenInput.value = details.location_value;
//   }

//   resetDeliveryShippingQuotesUI();

//   if (typeof saveDeliveryFormData === 'function') {
//     saveDeliveryFormData();
//   }
// }

function applyDeliveryPlaceDetails(details, { input, fillStreet, selectedLabel }) {
  const cityInput = document.querySelector('input[name="city"]');
  const postcodeInput = document.querySelector('input[name="delivery_postal_code"]');
  const stateInput = document.querySelector('input[name="state"]');
  const locationSearch = document.getElementById('location-search');
  const hiddenInput = document.getElementById('location_value');
  const streetInput = document.querySelector('input[name="delivery_address"]');

  if (fillStreet && streetInput) {
    streetInput.value = details.street_address || selectedLabel || '';
    lockDeliveryField(streetInput, 'delivery-address-input');
  } else if (input === streetInput) {
    input.value = details.street_address || selectedLabel || '';
    lockDeliveryField(input, 'delivery-address-input');
  }

  if (cityInput && details.city) cityInput.value = details.city;
  if (stateInput && details.state) stateInput.value = details.state;
  if (postcodeInput && details.postcode) postcodeInput.value = details.postcode;

  if (locationSearch && details.location_label) {
    locationSearch.value = details.location_label;
    lockDeliveryField(locationSearch, 'location-search');
  }

  if (hiddenInput && details.location_value) hiddenInput.value = details.location_value;

  resetDeliveryShippingQuotesUI();

  if (typeof saveDeliveryFormData === 'function') saveDeliveryFormData();
}

function lockDeliveryField(input, inputId) {
  if (!input) return;
  if (input.dataset.locked === 'true') return;

  input.setAttribute('readonly', 'true');
  input.dataset.locked = 'true';
  input.style.background = '#f5f5f5';

  // Cross button sirf street address field pe lagao
  if (inputId === 'delivery-address-input') {
    const wrapper = input.parentElement;
    if (wrapper && !wrapper.querySelector('.clear-delivery-field')) {
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'clear-delivery-field';
      clearBtn.innerHTML = '&times;';
      clearBtn.style.cssText = 'position:absolute;right:10px;top:45%;transform:translateY(-50%);background:none;border:none;cursor:pointer;font-size:16px;color:#888;line-height:1;';

      clearBtn.addEventListener('click', function () {
        unlockDeliveryField(input);

        // Street clear hone pe location-search aur hidden fields bhi clear karo
        const locationSearch = document.getElementById('location-search');
        const cityInput = document.querySelector('input[name="city"]');
        const postcodeInput = document.querySelector('input[name="delivery_postal_code"]');
        const stateInput = document.querySelector('input[name="state"]');
        const locationValue = document.getElementById('location_value');

        if (locationSearch) {
          unlockDeliveryField(locationSearch);
          locationSearch.value = '';
        }
        if (cityInput) cityInput.value = '';
        if (postcodeInput) postcodeInput.value = '';
        if (stateInput) stateInput.value = '';
        if (locationValue) locationValue.value = '';

        resetDeliveryShippingQuotesUI();
        resetDeliveryInstructions();
      });

      wrapper.appendChild(clearBtn);
    }
  }
}

function unlockDeliveryField(input) {
  if (!input) return;
  input.removeAttribute('readonly');
  input.dataset.locked = 'false';
  input.style.background = '';
  input.value = '';
  input.focus();

  const clearBtn = input.parentElement?.querySelector('.clear-delivery-field');
  if (clearBtn) clearBtn.remove();
}

function resetDeliveryShippingQuotesUI() {
  const container = document.getElementById('shippingQuotes');

  if (container) {
    container.innerHTML = '';
  }

  const deliveryType = document.querySelector('input[name="delivery_type"]:checked')?.value || '';

  if (deliveryType === 'delivered') {
    document.querySelector('.confirm-btn') && (document.querySelector('.confirm-btn').style.display = 'block');
  }

  const placeOrderBtn = document.querySelector('#placeOrderBtn');

  if (placeOrderBtn) {
    placeOrderBtn.style.display = 'none';
  }
}

function resetDeliveryInstructions() {
  const container = document.getElementById('deliveryInstructions');

  if (container) {
    container.innerHTML = '';
  }
}

function renderDeliveryInstructions() {
  const container = document.getElementById('deliveryInstructions');
  if (!container) return;

  container.innerHTML = `
    <div class="delivery-instructions-info">
      <h2 class="red">Important note on delivery options</h2>
      <p>
        If you need <b>assistance to get your safe from the delivery truck to a ground-level entry with NO stairs/steps in the path</b>
        you MUST select:
      </p>
      <ul>
        <li>FedEx (TNT) Technology Express - Sensitive</li>
      </ul>
      <p>
        If you need <b>assistance to get your safe from the delivery truck to a NON-ground level location or THERE ARE stairs/steps in the path</b> you MUST:
      </p>
      <ul>
        <li>scroll up and select "Delivery & Installation" and we will provide a quote</li>
      </ul>
      <p>
        If you will be removing the safe from the delivery truck yourself (please consider the weight of your item) please choose ANY freight options below.
      </p>
    </div>
  `;
}

function renderQuotes(quotes) {

  const container = document.getElementById('shippingQuotes');
  container.innerHTML = '';

  if (!quotes.length) {
    container.innerHTML = `
    <p class="no-shipping-info">
      Shipping information not currently available.
      <button type="button" id="deliveryplaceorderBtn" class="link-btn">
        Continue without shipping estimate
      </button>
    </p>
    `;
    
    return;
  }
  //   if (!quotes.length) {
  //   container.innerHTML = `
  //   <p class="no-shipping-info">
  //     <b>No products found in this Shipping Location.
  //     <button type="button" id="deliveryplaceorderBtn" class="link-btn">
  //      Continue without shipping estimate
  //     </button></
  //   </p>
  //   `;
    
  //   return;
  // }

  // quotes.forEach((quote, index) => {

  //   const option = `
  //     <label class="shipping-option">
  //      <span>  <input 
  //         type="radio" 
  //         name="shipping_service" 
  //         value="${index}"
  //         data-total="${quote.total}"
  //         data-service="${quote.service.serviceName}"
  //       />
  //       <strong>${quote.service.serviceName}</strong> </span>
  //       $${quote.total.toFixed(2)}
  //     </label>
  //   `;

  //   container.innerHTML += option;
  // });
  quotes.forEach((quote, index) => {

    // add 20%
    let updatedTotal = parseFloat(quote.total * 1.2);

    const option = `
      <label class="shipping-option">
        <span>
          <input 
            type="radio" 
            name="shipping_service" 
            value="${index}"
            data-total="${updatedTotal.toFixed(2)}"
            data-service="${quote.service.serviceName}"
          />
          <strong>${quote.service.serviceName}</strong>
        </span>
        $${updatedTotal.toFixed(2)}
      </label>
    `;

    container.innerHTML += option;
  });

  document.getElementById('placeOrderBtn').style.display = 'block';
}

/*document.getElementById('placeOrderBtn').addEventListener('click', function () {

  const selected = document.querySelector('input[name="shipping_service"]:checked');

  if (!selected) {
    alert('Please select a shipping option');
    return;
  }

  const shippingData = {
    total: parseFloat(selected.dataset.total),
    service_name: selected.dataset.service
  };
  const totalText = document.querySelector("#total-cart-price").innerText;
  const numericValue = parseFloat(totalText.replace(/[^0-9.]/g, ''));
  const finalTotal = numericValue + shippingData.total;
  document.querySelector("#shipping-price").innerText = `$${shippingData.total.toFixed(2)}`;
  document.querySelector('#total-cart-price').innerText = `$${finalTotal.toFixed(2)}`;
  document.getElementById("request-wrapper").style.display = "block";
  document.querySelector("#createRequestOrder .btn-text").innerText = "Request Quote";
  document.querySelector('[name="deliver_service_price"]').value = shippingData.total.toFixed(2);
  document.querySelector('[name="deliver_service_name"]').value = shippingData.service_name;
  document.querySelector("#post-code").innerText = 'To ' + document.querySelector('[name="location_value"]').value;
  document.getElementById("delivery-popup").style.display = "none";

});*/


/*document.addEventListener('click', function (e) {
  const placeOrderBtn = e.target.closest('#placeOrderBtn');

  if (!placeOrderBtn) return;

  const selected = document.querySelector('input[name="shipping_service"]:checked');

  if (!selected) {
    alert('Please select a shipping option');
    return;
  }

  const shippingData = {
    total: parseFloat(selected.dataset.total || 0),
    service_name: selected.dataset.service || ''
  };

  const totalPriceEl = document.querySelector("#total-cart-price");
  const shippingPriceEl = document.querySelector("#shipping-price");
  const requestWrapper = document.getElementById("request-wrapper");
  const requestBtnText = document.querySelector("#createRequestOrder .btn-text");
  const deliveryServicePrice = document.querySelector('[name="deliver_service_price"]');
  const deliveryServiceName = document.querySelector('[name="deliver_service_name"]');
  const postCodeEl = document.querySelector("#post-code");
  const locationValue = document.querySelector('[name="location_value"]');
  const deliveryPopup = document.getElementById("delivery-popup");

  if (!totalPriceEl) return;

  const totalText = totalPriceEl.innerText || '0';
  const numericValue = parseFloat(totalText.replace(/[^0-9.]/g, '')) || 0;
  const finalTotal = numericValue + shippingData.total;

  if (shippingPriceEl) {
    shippingPriceEl.innerText = `$${shippingData.total.toFixed(2)}`;
  }

  totalPriceEl.innerText = `$${finalTotal.toFixed(2)}`;

  if (requestWrapper) {
    requestWrapper.style.display = "block";
  }

  if (requestBtnText) {
    requestBtnText.innerText = "Request Quote";
  }

  if (deliveryServicePrice) {
    deliveryServicePrice.value = shippingData.total.toFixed(2);
  }

  if (deliveryServiceName) {
    deliveryServiceName.value = shippingData.service_name;
  }

  if (postCodeEl && locationValue) {
    postCodeEl.innerText = 'To ' + locationValue.value;
  }

  if (deliveryPopup) {
    deliveryPopup.style.display = "none";
  }
});*/

document.addEventListener('click', async function (e) {
  const placeOrderBtn = e.target.closest('#placeOrderBtn');
  if (!placeOrderBtn) return;

  const selected = document.querySelector('input[name="shipping_service"]:checked');

  if (!selected) {
    alert('Please select a shipping option');
    return;
  }

  const shippingData = {
    total: parseFloat(selected.dataset.total || 0),
    service_name: selected.dataset.service || ''
  };

  const totalPriceEl = document.querySelector("#total-cart-price");
  const shippingPriceEl = document.querySelector("#shipping-price");
  const deliveryServicePrice = document.querySelector('[name="deliver_service_price"]');
  const deliveryServiceName = document.querySelector('[name="deliver_service_name"]');
  const postCodeEl = document.querySelector("#post-code");
  const locationValue = document.querySelector('[name="location_value"]');
  const deliveryPopup = document.getElementById("delivery-popup");

  const totalText = totalPriceEl?.innerText || '0';
  const numericValue = parseFloat(totalText.replace(/[^0-9.]/g, '')) || 0;
  const finalTotal = numericValue + shippingData.total;

  if (shippingPriceEl) {
    shippingPriceEl.innerText = `$${shippingData.total.toFixed(2)}`;
  }

  if (totalPriceEl) {
    totalPriceEl.innerText = `$${finalTotal.toFixed(2)}`;
  }

  if (deliveryServicePrice) {
    deliveryServicePrice.value = shippingData.total.toFixed(2);
  }

  if (deliveryServiceName) {
    deliveryServiceName.value = shippingData.service_name;
  }

if (postCodeEl && locationValue) {
  const parts = locationValue.value.split('|').filter(Boolean);
  postCodeEl.innerText = 'To ' + parts.join(' ');
}

  const postcode =
    document.querySelector('[name="post_code"]')?.value ||
    document.querySelector('[name="delivery_postal_code"]')?.value ||
    locationValue?.value ||
    '';

  if (window.AuslockShipping?.persistShippingSelection) {
    window.AuslockShipping.persistShippingSelection(
      postcode,
      shippingData.service_name,
      shippingData.total.toFixed(2)
    );
  }

  if (window.AuslockShipping?.markDeliveryFlowComplete) {
    window.AuslockShipping.markDeliveryFlowComplete({
      popupDeliveryType: 'delivered',
      shippingUnavailable: false
    });
  }

  if (window.AuslockShipping?.saveDeliveryFormData) {
    window.AuslockShipping.saveDeliveryFormData();
  }

  const cart = await fetch('/cart.js').then((r) => r.json());

  if (window.AuslockShipping?.saveShippingCartFingerprint) {
    window.AuslockShipping.saveShippingCartFingerprint(cart);
  }

  if (deliveryPopup) {
    deliveryPopup.style.display = 'none';
  }

  if (window.AuslockShipping?.refreshCartActionButtons) {
    window.AuslockShipping.refreshCartActionButtons(cart);
  }
});



// document.getElementById('deliveryplaceorderBtn').addEventListener('click', function () {

//   console.log('ffffffffffffffffffffff');
//    const form = document.getElementById('delivery-form');

//     if (!form.checkValidity()) {
//       form.reportValidity();
//       return;
//     }

//     document.getElementById("request-wrapper").style.display = "block";
//     document.querySelector("#createRequestOrder .btn-text").innerText = "Checkout";
//     document.querySelector("#shipping-price").innerText = "TBA";
//     document.querySelector('[name="deliver_service_price"]').value = "";
//     document.querySelector('[name="deliver_service_name"]').value = "";
//     document.getElementById("delivery-popup").style.display = "none";
    

//     // const shippingData = {};
//     // createDraftOrder(shippingData);
// });

// document.addEventListener('click', function (e) {
//   if (e.target && e.target.id === 'deliveryplaceorderBtn') {

//     const form = document.getElementById('delivery-form');

//     if (!form.checkValidity()) {
//       form.reportValidity();
//       return;
//     }

//     const requestWrapper = document.getElementById("request-wrapper");
//     const requestBtn = document.getElementById("createRequestOrder");

//     if (window.AuslockShipping?.shouldShowRequestQuote?.()) {
//       if (requestWrapper) {
//         requestWrapper.style.display = "block";
//       }

//       if (requestBtn) {
//         requestBtn.style.display = "";
//       }
//     }

//     document.querySelector("#createRequestOrder .btn-text").innerText = "Request Quote";
//     document.querySelector("#shipping-price").innerText = "TBA";
//     document.querySelector("#post-code").innerText = 'To ' + document.querySelector('[name="location_value"]').value;
//     document.querySelector('[name="deliver_service_price"]').value = "";
//     document.querySelector('[name="deliver_service_name"]').value = "";
//     document.getElementById("delivery-popup").style.display = "none";
//   }
// });
document.addEventListener('click', function (e) {
  if (e.target && e.target.id === 'deliveryplaceorderBtn') {

    const form = document.getElementById('delivery-form');

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (window.AuslockShipping?.saveDeliveryFormData) {
      window.AuslockShipping.saveDeliveryFormData();
    }

    if (window.AuslockShipping?.markDeliveryFlowComplete) {
      window.AuslockShipping.markDeliveryFlowComplete({
        popupDeliveryType: document.querySelector('input[name="delivery_type"]:checked')?.value || 'delivered',
        shippingUnavailable: true
      });
    }

    document.querySelector("#shipping-price").innerText = "TBA";

    const locationValue = document.querySelector('[name="location_value"]');
    const postCodeEl = document.querySelector("#post-code");
if (postCodeEl && locationValue?.value) {
  const parts = locationValue.value.split('|').filter(Boolean);
  postCodeEl.innerText = 'To ' + parts.join(' ');
}

    document.querySelector('[name="deliver_service_price"]').value = "";
    document.querySelector('[name="deliver_service_name"]').value = "";

    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        if (window.AuslockShipping?.saveShippingCartFingerprint) {
          window.AuslockShipping.saveShippingCartFingerprint(cart);
        }

        document.getElementById("delivery-popup").style.display = "none";

        if (window.AuslockShipping?.refreshCartActionButtons) {
          window.AuslockShipping.refreshCartActionButtons(cart);
        }
      });
  }
});


/*document.getElementById('requestQuoteBtn').addEventListener('click', function () {

  const form = document.getElementById('delivery-form');

  if (!form.checkValidity()) {
    form.reportValidity();
    return;
  }

  document.querySelector("#createRequestOrder .btn-text").innerText = "Request Quote";
  document.getElementById("request-wrapper").style.display = "block";
  document.querySelector("#shipping-price").innerText = "TBA";
  document.querySelector("#post-code").innerText = 'To ' + document.querySelector('[name="location_value"]').value;
  document.querySelector('[name="deliver_service_price"]').value = "";
  document.querySelector('[name="deliver_service_name"]').value = "";
  document.getElementById("delivery-popup").style.display = "none";
    
  // const shippingData = {};
  // createDraftOrder(shippingData);
});*/
document.addEventListener('click', async function (e) {
  const requestQuoteBtn = e.target.closest('#requestQuoteBtn');

  if (!requestQuoteBtn) return;

  const form = document.getElementById('delivery-form');

  if (form && !form.checkValidity()) {
    form.reportValidity();
    return;
  }

  const shippingPriceEl = document.querySelector("#shipping-price");
  const postCodeEl = document.querySelector("#post-code");
  const locationValue = document.querySelector('[name="location_value"]');
  const deliveryServicePrice = document.querySelector('[name="deliver_service_price"]');
  const deliveryServiceName = document.querySelector('[name="deliver_service_name"]');
  const deliveryPopup = document.getElementById("delivery-popup");

  if (window.AuslockShipping?.saveDeliveryFormData) {
    window.AuslockShipping.saveDeliveryFormData();
  }

  if (window.AuslockShipping?.markDeliveryFlowComplete) {
    window.AuslockShipping.markDeliveryFlowComplete({
      popupDeliveryType: 'delivery_install',
      shippingUnavailable: true
    });
  }

  if (window.AuslockShipping?.refreshCartActionButtons) {
    window.AuslockShipping.refreshCartActionButtons();
  }

  if (shippingPriceEl) {
    shippingPriceEl.innerText = "TBA";
  }

if (postCodeEl && locationValue) {
  const parts = locationValue.value.split('|').filter(Boolean);
  postCodeEl.innerText = 'To ' + parts.join(' ');
}

  if (deliveryServicePrice) {
    deliveryServicePrice.value = "";
  }

  if (deliveryServiceName) {
    deliveryServiceName.value = "";
  }

  const cart = await fetch('/cart.js').then((r) => r.json());

  if (window.AuslockShipping?.saveShippingCartFingerprint) {
    window.AuslockShipping.saveShippingCartFingerprint(cart);
  }

  if (deliveryPopup) {
    deliveryPopup.style.display = "none";
  }

  if (window.AuslockShipping?.refreshCartActionButtons) {
    window.AuslockShipping.refreshCartActionButtons(cart);
  }
});

/*document.getElementById('createRequestOrder').addEventListener('click', function () {
    createDraftOrder();
});*/
// document.addEventListener('click', function (e) {
//   const createRequestOrderBtn = e.target.closest('#createRequestOrder');

//   if (!createRequestOrderBtn) return;

//   createDraftOrder();
// });



/*async function createDraftOrder() {

  
  try {
    showLoader();
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    if (!cart.items.length) {
      alert("Cart is empty");
      return;
    }

    const lineItems = cart.items.map(item => ({
      variant_id: item.variant_id,
      quantity: item.quantity,
      price : item.price / 100
    }));

    const form = document.querySelector('#delivery-form');

    const deliveryAddress = {
      email: form.querySelector('input[name="contact_email"]')?.value || "",
      residential_type: form.querySelector('input[name="residential_type_value"]:checked')?.value || "",
      name: form.querySelector('input[name="contact_name"]')?.value || "",
      streetName: form.querySelector('input[name="delivery_address"]')?.value || "",
       companyName: form.querySelector('input[name="company_name"]')?.value || "",

      locality: form.querySelector('input[name="city"]')?.value || "",
      state: form.querySelector('input[name="state"]')?.value || "",
      postcode: form.querySelector('input[name="delivery_postal_code"]')?.value || "",
      country: "AU",
      lift_available: form.querySelector('input[name="lift_available"]:checked')?.value || "",
      floor_level: form.querySelector('input[name="floor_level"]:checked')?.value || "",
      floor_level_other: form.querySelector('input[name="floor_level_other"]')?.value || "",
      special_info: form.querySelector('textarea[name="special_info"]')?.value || "",
    };
    const deliveryType = document.querySelector('input[name="delivery_type"]:checked')?.value || "";
    const shop = form.querySelector('input[name="shop_name"]')?.value;

    const deliverPrice = form.querySelector('input[name="deliver_service_price"]')?.value;

    let payload = {
      shop,
      line_items: lineItems,
      delivery_address: deliveryAddress,
      delivery_type: deliveryType
    };

    if (deliverPrice) {
      payload.shipping_data = {
        total: deliverPrice,
        service_name: form.querySelector('input[name="deliver_service_name"]')?.value || ""
      };
    }
    // const shippingData = {
    //   total: form.querySelector('input[name="deliver_service_price"]')?.value || "",
    //   service_name: form.querySelector('input[name="deliver_service_name"]')?.value || "",
    // };

    // const payload = {
    //   shop,
    //   line_items: lineItems,
    //   delivery_address: deliveryAddress,
    //   delivery_type : deliveryType,
    //   shipping_data : shippingData
    // };

    console.log("Draft Order Payload:", payload);

    const response = await fetch('https://api.auslock.com.au/api/create-draft-order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();
    console.log(result);
    return false;

    if (!response.ok || !result.success) {
      alert(result.message || "Error creating request");
      window.location.href = "/";
      return;
    }

    await clearCart();
    hideLoader();
    // alert("Request created successfully");
    window.location.href = "/pages/quote-request-confirmation";

  } catch (error) {
    hideLoader();
    console.error("Draft order error:", error);
    alert(error.message || "Error creating request");
    window.location.href = "/";
  }

}*/

async function createDraftOrder(redirectToConfirmation = true) {
  setRequestQuoteButtonLoading(true);

  try {
    showLoader();

    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    if (!cart.items.length) {
      alert("Cart is empty");
      return false;
    }

    const lineItems = cart.items.map(item => ({
      variant_id: item.variant_id,
      quantity: item.quantity,
      price: item.price / 100
    }));

    const form = document.querySelector('#delivery-form');

    const deliveryAddress = getDeliveryFormAddress(form);

    // if (!deliveryAddress.phone) {
    //   alert('Please enter a valid Australian contact number (e.g. 0412345678).');
    //   return false;
    // }

    const deliveryType =
      localStorage.getItem('popup_delivery_type') ||
      document.querySelector('input[name="delivery_type"]:checked')?.value ||
      '';
    const shop = form?.querySelector('input[name="shop_name"]')?.value || '';
    const deliverPrice = form?.querySelector('input[name="deliver_service_price"]')?.value || '';

    const mainDeliveryType =
      localStorage.getItem('main_delivery_type') ||
      document.querySelector('input[name="main_delivery_type"]:checked')?.value ||
      'pickup';
    let payload = {
      shop,
      line_items: lineItems,
      delivery_type: deliveryType,
      main_delivery_type: mainDeliveryType,
      is_request_quote: redirectToConfirmation,
    };

    if (mainDeliveryType !== 'pickup') {
      payload.delivery_address = deliveryAddress;

      if (deliverPrice) {
        payload.shipping_data = {
          total: deliverPrice,
          service_name: form?.querySelector('input[name="deliver_service_name"]')?.value || ""
        };
      }
    }

    const response = await fetch('https://api.auslock.com.au/api/create-draft-order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      alert(result.message || "Error creating draft order");
      return false;
    }

    const invoiceUrl = result.invoice_url || result.invoiceUrl || result.draft_order?.invoice_url;

    if (!invoiceUrl) {
      alert("Draft order created but invoice URL was not returned from API.");
      return false;
    }

    if (redirectToConfirmation) {
      await clearCart();
      window.location.href = "/pages/quote-request-confirmation";
      return true;
    }

    return invoiceUrl;

  } catch (error) {
    console.error("Draft order error:", error);
    alert(error.message || "Error creating draft order");
    return false;
  } finally {
    hideLoader();
    setRequestQuoteButtonLoading(false);
  }
}

document.addEventListener('click', async function (e) {
  const requestQuoteBtn = e.target.closest('#createRequestOrder');
  if (!requestQuoteBtn) return;

  await createDraftOrder();
});

document.addEventListener('click', function (e) {
  const checkoutBtn = e.target.closest('#draftCheckoutBtn');
  if (!checkoutBtn) return;

  if (!window.draftInvoiceUrl) {
    alert("Invoice URL not available. Please select shipping again.");
    return;
  }

  window.location.href = window.draftInvoiceUrl;
});


async function clearCart() {
  await fetch('/cart/clear.js', { method: 'POST' });
}


//const shippingText = document.getElementById("shipping-price");



/* ==============================
   Product Page Delivery Estimate Form
============================== */

document.addEventListener('DOMContentLoaded', function () {
  const estimateBox = document.querySelector('.delivery-estimate-box');

  if (!estimateBox) {
    return;
  }

  const locationInput = estimateBox.querySelector('#estimate_location');
  const stateInput = estimateBox.querySelector('#estimate_state');
  const postcodeInput = estimateBox.querySelector('#estimate_postcode');
  const locationValueInput = estimateBox.querySelector('#estimate_location_value');
  const locationLabelInput = estimateBox.querySelector('#estimate_location_label');
  const suggestionsBox = estimateBox.querySelector('#estimate-location-suggestions');

  const residentialTypeInputs = estimateBox.querySelectorAll('input[name="residential_type_value"]');
  let residentialType = 'residential';

  const minus = estimateBox.querySelector('.qty-btn.minus');
  const plus = estimateBox.querySelector('.qty-btn.plus');
  const qtyInput = estimateBox.querySelector('#estimate_qty');
  const estimateBtn = estimateBox.querySelector('#get-estimate-btn');
  const resultBox = estimateBox.querySelector('#estimate-result');

  let debounceTimeout = null;

  if (minus && qtyInput) {
    minus.addEventListener('click', function () {
      let val = parseInt(qtyInput.value, 10) || 1;

      if (val > 1) {
        qtyInput.value = val - 1;
      }
    });
  }

  if (plus && qtyInput) {
    plus.addEventListener('click', function () {
      let val = parseInt(qtyInput.value, 10) || 1;
      qtyInput.value = val + 1;
    });
  }

  residentialTypeInputs.forEach(residentialTypeField => {
    residentialTypeField.addEventListener('change', (event) => {
      if (resultBox && resultBox.innerHTML !== '') {
        console.log('Selected residential type:', event.target.value);
        residentialType = event.target.value;
        resultBox.innerHTML = '';
        suggestionsBox.innerHTML = '';
      }
    });
  });

  if (locationInput && suggestionsBox) {
    locationInput.addEventListener('input', function () {
      clearTimeout(debounceTimeout);

      const query = locationInput.value.trim();

      postcodeInput.value = '';
      stateInput.value = '';
      locationValueInput.value = '';
      locationLabelInput.value = '';

      if (resultBox) {
        resultBox.innerHTML = '';
      }

      if (query.length < 2) {
        suggestionsBox.innerHTML = '';
        suggestionsBox.style.display = 'none';
        return;
      }

      debounceTimeout = setTimeout(async function () {
        try {
          const response = await fetch(
            `https://api.auslock.com.au/api/suburbs?q=${encodeURIComponent(query)}`
          );

          const results = await response.json();

          console.log(results);

          suggestionsBox.innerHTML = '';

          if (!results || !results.length) {
            suggestionsBox.innerHTML = `
              <div class="suggestion-item no-result">
                No location found
              </div>
            `;
            suggestionsBox.style.display = 'block';
            return;
          }

          results.forEach(function (item) {
            const div = document.createElement('div');
            div.classList.add('suggestion-item');

            div.textContent = item.label;

            div.addEventListener('click', function () {
              const parts = item.value.split('|');

              // Example item.value: WEST HOXTON|2171|NSW
              const location = parts[0] || '';
              const state = parts[1] || '';
              const postcode = parts[2] || '';

              // Input shows full label: WEST HOXTON 2171
              locationInput.value = item.label;

              // Hidden values for payload
              locationValueInput.value = location;
              postcodeInput.value = postcode;
              stateInput.value = state;
              locationLabelInput.value = item.label;

              suggestionsBox.innerHTML = '';
              suggestionsBox.style.display = 'none';

              if (resultBox) {
                resultBox.innerHTML = '';
              }
            });

            suggestionsBox.appendChild(div);
          });

          suggestionsBox.style.display = 'block';

        } catch (error) {
          console.error('Product page location search error:', error);

          suggestionsBox.innerHTML = `
            <div class="suggestion-item no-result">
              Error loading locations
            </div>
          `;

          suggestionsBox.style.display = 'block';
        }
      }, 400);
    });

    document.addEventListener('click', function (e) {
      if (!estimateBox.contains(e.target)) {
        suggestionsBox.innerHTML = '';
        suggestionsBox.style.display = 'none';
      }
    });
  }

  if (estimateBtn) {
    estimateBtn.addEventListener('click', async function () {
      const qty = parseInt(qtyInput.value, 10) || 1;

      const location = locationValueInput.value;
      const postcode = postcodeInput.value;
      const state = stateInput.value;
      const locationLabel = locationLabelInput.value || locationInput.value;
      
      if (!location || !postcode) {
        resultBox.innerHTML = `
          <div class="estimate-response">
            <p class="estimate-error">
              Please select a valid postcode from suggestions.
            </p>
          </div>
        `;
        return;
      }

      resultBox.innerHTML = `
        <div class="estimate-response">
          Loading...
        </div>
      `;

      estimateBtn.disabled = true;

      try {
        const productId = estimateBox.dataset.productId;
        const variantId = estimateBox.dataset.variantId;
        const sku = estimateBox.dataset.sku;
        const grams = parseInt(estimateBox.dataset.grams, 10) || 0;
        const shop = estimateBox.dataset.shop || window.Shopify?.shop || '';

        const deliveryAddress = {
          residential_type: residentialType,
          name: '',
          streetName: '',
          companyName: '',
          locality: location,
          state: state,
          postcode: postcode,
          country: 'AU'
        };

        const cartItems = [
          {
            product_id: Number(productId),
            variant_id: Number(variantId),
            sku: sku,
            quantity: qty,
            grams: grams
          }
        ];

        const payload = {
          deliveryAddress: deliveryAddress,
          cartItems: cartItems,
          shop: shop
        };

        console.log('Product Estimate Payload:', payload);

        const response = await fetch('https://api.auslock.com.au/api/get-quotes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json();

        console.log('Product Estimate Result:', result);

        renderProductEstimateQuotes(result, qty, locationLabel);

      } catch (error) {
        console.error('Product estimate error:', error);

        resultBox.innerHTML = `
          <div class="estimate-response">
            <p class="estimate-error">
              Shipping information not currently available.
            </p>
          </div>
        `;
      } finally {
        estimateBtn.disabled = false;
      }
    });
  }
});


/*function renderProductEstimateQuotes(quotes, qty, locationLabel) {
  const resultBox = document.getElementById('estimate-result');

  if (!resultBox) {
    return;
  }

  resultBox.innerHTML = '';

  if (!quotes || !quotes.length) {
    resultBox.innerHTML = `
      <div class="estimate-response">
        <h4>Deliver Qty ${qty} to ${locationLabel}</h4>

        <p class="no-shipping-info">
          Shipping information not currently available.
        </p>
      </div>
    `;
    return;
  }

  let html = `
    <div class="estimate-response">
      <h4>Deliver Qty ${qty} to ${locationLabel}</h4>
  `;

  quotes.forEach(function (quote) {
    const serviceName = quote?.service?.serviceName || 'Shipping Service';

    const isInstallation =
      serviceName.toLowerCase().includes('installation') ||
      serviceName.toLowerCase().includes('full delivery');

    const updatedTotal = parseFloat(quote.total * 1.2);

    const displayPrice = isInstallation || isNaN(updatedTotal)
      ? 'TBA'
      : '$' + updatedTotal.toFixed(2);

    let extraNote = '';

    if (isInstallation) {
      extraNote = `
        <div class="rate-note">
          Delivered and positioned into location.<br>
          Professional installation of your safe
        </div>
      `;
    }

    html += `
      <div class="rate-row">
        <div class="rate-left">
          <div class="rate-name">${serviceName}</div>
          ${extraNote}
        </div>

        <div class="rate-price">
          ${displayPrice}
        </div>
      </div>
    `;
  });

  html += `</div>`;

  resultBox.innerHTML = html;
}*/

function renderProductEstimateQuotes(quotes, qty, locationLabel) {
  const resultBox = document.getElementById('estimate-result');

  if (!resultBox) {
    return;
  }

  resultBox.innerHTML = '';

  if (!quotes || !quotes.length) {
    resultBox.innerHTML = `
      <div class="estimate-response">
        <h4>Deliver Qty ${qty} to ${locationLabel}</h4>

        <p class="no-shipping-info">
          Shipping information not currently available.
        </p>
      </div>
    `;
    return;
  }

  let html = `
    <div class="estimate-response">
      <h4>Deliver Qty ${qty} to ${locationLabel}</h4>
  `;

  let hasInstallationService = false; // ✅ NEW FLAG

  quotes.forEach(function (quote) {
    const serviceName = quote?.service?.serviceName || 'Shipping Service';

    const isInstallation =
      serviceName.toLowerCase().includes('installation') ||
      serviceName.toLowerCase().includes('full delivery');

    if (isInstallation) {
      hasInstallationService = true; // ✅ MARK FOUND
    }

    const updatedTotal = parseFloat(quote.total * 1.2);

    const displayPrice = isInstallation || isNaN(updatedTotal)
      ? 'TBA'
      : '$' + updatedTotal.toFixed(2);

    let extraNote = '';

    if (isInstallation) {
      extraNote = `
        <div class="rate-note">
          Delivered and positioned into location.<br>
          Professional installation of your safe
        </div>
      `;
    }

    html += `
      <div class="rate-row">
        <div class="rate-left">
          <div class="rate-name">${serviceName}</div>
          ${extraNote}
        </div>

        <div class="rate-price">
          ${displayPrice}
        </div>
      </div>
    `;
  });

  // ✅ ADD ONLY IF NOT PRESENT
  if (!hasInstallationService) {
    html += `
      <div class="rate-row">
        <div class="rate-left">
          <div class="rate-name">Full Delivery &amp; Installation Service</div>
          <div class="rate-note">
            Delivered and positioned into location.<br>
            Professional installation of your safe
          </div>
        </div>

        <div class="rate-price">
          TBA
        </div>
      </div>
    `;
  }

  html += `</div>`;

  resultBox.innerHTML = html;
}

document.addEventListener('DOMContentLoaded', function () {
  const openBtn = document.querySelector('.estimate-link');
  const popup = document.getElementById('deliveryInstructionPopup');
  const closeBtn = document.querySelector('.delivery-popup-close');

  // stop if popup elements not found
  if (!popup) return;

  // Open popup
  openBtn.addEventListener('click', function () {
    popup.classList.add('active');
  });

  // Close via X
  closeBtn.addEventListener('click', function () {
    popup.classList.remove('active');
  });

  // Close when clicking outside
  popup.addEventListener('click', function (e) {
    if (e.target === popup) {
      popup.classList.remove('active');
    }
  });
});

/*document.addEventListener('click', async function (e) {
  const pickupBtn = e.target.closest('#pickupDraftCheckoutBtn');
  if (!pickupBtn) return;
  const invoiceUrl = await createDraftOrder(false);

  if (invoiceUrl) {
    await clearCart();
    window.location.href = invoiceUrl;
  }
});*/


document.addEventListener('click', async function (e) {
  const pickupBtn = e.target.closest('#pickupDraftCheckoutBtn');
  if (!pickupBtn) return;

  e.preventDefault();

  pickupBtn.disabled = true;
  //pickupBtn.classList.add('loading');
  //showLoader();

  try {
    const invoiceUrl = await createDraftOrder(false);

    if (invoiceUrl) {
      //await clearCart();
      //hideLoader();
      window.location.href = invoiceUrl;
      return;
    }

    //hideLoader();
    pickupBtn.disabled = false;
    //pickupBtn.classList.remove('loading');

  } catch (error) {
    console.error(error);
    //hideLoader();
    pickupBtn.disabled = false;
    //pickupBtn.classList.remove('loading');
    alert('Something went wrong. Please try again.');
  }
});

(function lockShippingSelection() {
  const container = document.getElementById('shippingQuotes');
  if (!container) return;

  let userChoice = null;
  let fixing = false;
  container.addEventListener('change', function (e) {
    if (e.target && e.target.name === 'shipping_service') {
      userChoice = e.target.value;
      enforce();
    }
  }, true);
  function enforce() {
    if (fixing) return;
    fixing = true;

    const radios = container.querySelectorAll('input[name="shipping_service"]');
    let target = userChoice
      ? container.querySelector(`input[name="shipping_service"][value="${userChoice}"]`)
      : radios[0];
    if (target) {
      radios.forEach(r => {
        const shouldBe = (r === target);
        if (r.checked !== shouldBe) r.checked = shouldBe;
        if (!shouldBe && r.hasAttribute('checked')) r.removeAttribute('checked');
        if (shouldBe && !r.hasAttribute('checked')) r.setAttribute('checked', 'checked');
      });
    }
    setTimeout(() => { fixing = false; }, 0);
  }
  const observer = new MutationObserver(() => enforce());
    observer.observe(container, {
      subtree: true,
      attributes: true,
      attributeFilter: ['checked']
    });
    enforce();
    window.enforceShippingSelection = enforce;
})();
